import React, { PropsWithChildren, useCallback } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { formatISO } from 'date-fns';
import { useCreateBusline } from '@area-restrita/api-client/endpoints/buslines/buslines.gen';

import { Form } from '@/components/ui/form';
import { useCompanySelectorStore } from '@/stores/companySelectorStore';

import { IBuslineSchema, buslineSchema } from '../BuslineForm';

import AddBuslineDialogView from './view';

interface IProps extends PropsWithChildren {}

const AddBuslineDialog: React.FC<IProps> = ({ children }) => {
  const selectedCompany = useCompanySelectorStore((s) => s.selectedCompany);
  const form = useForm<IBuslineSchema>({
    defaultValues: {
      company: {
        label: selectedCompany?.tradingName,
        value: selectedCompany?.id,
      },
    },
    resolver: zodResolver(buslineSchema),
  });

  const createBusline = useCreateBusline({
    mutation: { onSuccess: () => form.reset() },
  });

  const handleSubmit = useCallback<SubmitHandler<IBuslineSchema>>(
    ({ company, fleet, checkList, startAt, endAt, daysOfWeek, ...data }) =>
      createBusline.mutateAsync({
        data: {
          fleetId: fleet.value,
          checklistId: checkList?.value,
          companyId: company.value,
          schedules: [
            {
              startAt: formatISO(startAt).split('T')[1].slice(0, -1),
              endAt: formatISO(endAt).split('T')[1].slice(0, -1),
              daysOfWeek,
            },
          ],
          ...data,
        },
      }),
    [createBusline],
  );

  return (
    <Form {...form}>
      <AddBuslineDialogView
        control={form.control}
        isLoading={createBusline.isPending}
        onSubmit={form.handleSubmit(handleSubmit)}
      >
        {children}
      </AddBuslineDialogView>
    </Form>
  );
};

export default AddBuslineDialog;
